/**
 * Nombres, montants et pourcentages tels qu'ils s'AFFICHENT dans l'espace
 * paroisse : un seul formateur pour toutes les pages.
 *
 * Pourquoi ne pas laisser faire `toLocaleString("fr-FR")` : depuis ICU 72, la
 * locale française groupe les milliers avec l'espace FINE insécable (U+202F).
 * Elle est si étroite qu'à l'écran « 81 500 FCFA » se lit « 81500 FCFA », et
 * son rendu dépend de la police et du navigateur : au tournage des vidéos de
 * formation, une ligne de la liste Mobile Money affichait « 1000 F » quand les
 * autres affichaient « 1 000 F », avec le même code. `toLocaleString()` sans
 * locale, lui, suit la langue du navigateur (« 1,000 » en anglais).
 * Même constat et même règle que les applications mobiles (lib/money.ts) :
 * on groupe à la main, avec l'espace insécable ORDINAIRE (U+00A0), visible et
 * qui ne laisse pas un nombre se couper en fin de ligne.
 *
 * Décimales à la française : virgule. Pourcentages : « +263,3 % », espace
 * insécable avant le signe.
 */
export const INSECABLE = " ";

/**
 * « 81 500 », « 1 234,5 » : milliers séparés par une espace insécable,
 * virgule décimale, `decimales` chiffres après la virgule (0 par défaut).
 * Valeur absente ou illisible : « · », le signe « non renseigné » de
 * l'application.
 */
export function nombre(valeur: number | string | null | undefined, decimales = 0): string {
  const n = typeof valeur === "string" ? Number(valeur) : valeur;
  if (n === null || n === undefined || !Number.isFinite(n)) return "·";
  const arrondi = Math.abs(n).toFixed(decimales);
  const [entier, fraction] = arrondi.split(".");
  const groupe = entier.replace(/\B(?=(\d{3})+(?!\d))/g, INSECABLE);
  // Pas de « -0 » quand l'arrondi tombe à zéro.
  const signe = n < 0 && Number(arrondi) !== 0 ? "-" : "";
  return `${signe}${groupe}${fraction ? `,${fraction}` : ""}`;
}

/**
 * Nombre qui peut porter des décimales utiles (heures de service : « 12,5 »,
 * « 2,25 », « 3 ») : au plus `maxDecimales` chiffres après la virgule, sans
 * zéros inutiles. `nombre()` arrondit à l'entier par défaut, ce qui
 * transformerait 12,5 heures en « 13 ».
 */
export function nombreDecimal(valeur: number | string | null | undefined, maxDecimales = 2): string {
  const texte = nombre(valeur, maxDecimales);
  return texte.includes(",") ? texte.replace(/,?0+$/, "") : texte;
}

/**
 * Montant en francs CFA : « 81 500 FCFA ». Arrondi au franc (le franc CFA n'a
 * pas de subdivision). `unite` garde l'unité que l'écran affichait déjà
 * (« FCFA » par défaut, « F » dans les listes serrées).
 */
export function montant(valeur: number | string | null | undefined, unite = "FCFA"): string {
  return `${nombre(valeur)}${INSECABLE}${unite}`;
}

/**
 * Montant abrégé pour les tuiles et graphiques serrés : « 1,2 M », « 212 k »,
 * « 950 ». Remplace les « 1.2M » et « 212K » au format anglais. L'unité
 * monétaire, quand l'écran en met une, est ajoutée par l'appelant.
 */
export function nombreCompact(valeur: number | string | null | undefined): string {
  const n = typeof valeur === "string" ? Number(valeur) : valeur;
  if (n === null || n === undefined || !Number.isFinite(n)) return "·";
  const abs = Math.abs(n);
  const signe = n < 0 ? "-" : "";
  // 999 600 s'abrège « 1,0 M » et non « 1 000 k ».
  if (abs >= 999_500) return `${signe}${nombre(abs / 1_000_000, 1)}${INSECABLE}M`;
  if (abs >= 1_000) return `${signe}${nombre(abs / 1_000, 0)}${INSECABLE}k`;
  return nombre(n);
}

/** Montant abrégé avec son unité : « 1,2 M FCFA », « 212 k FCFA ». */
export function montantCompact(valeur: number | string | null | undefined, unite = "FCFA"): string {
  return `${nombreCompact(valeur)}${INSECABLE}${unite}`;
}

/**
 * Pourcentage à la française : « 91,6 % », « +263,3 % » avec `signe`.
 * `decimales` chiffres après la virgule (1 par défaut). La valeur est déjà
 * en pour cent (91.6, pas 0.916).
 */
export function pourcentage(
  valeur: number | string | null | undefined,
  { decimales = 1, signe = false }: { decimales?: number; signe?: boolean } = {},
): string {
  const n = typeof valeur === "string" ? Number(valeur) : valeur;
  if (n === null || n === undefined || !Number.isFinite(n)) return "·";
  const texte = nombre(n, decimales);
  const plus = signe && n > 0 && Number(Math.abs(n).toFixed(decimales)) !== 0 ? "+" : "";
  return `${plus}${texte}${INSECABLE}%`;
}
